/**
 * Door hanger responses sent in from the QR code on the door hanger (Jotform for now).
 * Most attach to their paper automatically; the rest wait here for the clerk.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const when = (d: string) => new Date(d).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
type Tab = 'unmatched' | 'matched' | 'dismissed';

export function DoorHangers() {
  const { path, navigate } = useRouter();
  const { can } = useAuth();
  const tab = ((new URLSearchParams(path.split('?')[1] ?? '').get('tab') as Tab) || 'unmatched') as Tab;
  const r = useLoad(() => api.get<any>(`/api/door-hangers?status=${tab}`), [tab]);
  const a = useAction();
  const [paperFor, setPaperFor] = useState<Record<string, string>>({});
  const manage = can('papers.intake');

  const match = (id: string, paper: string) =>
    a.run(async () => {
      await api.post(`/api/door-hangers/${id}/match`, { paper });
      r.reload();
    });
  const dismiss = (id: string) => {
    const reason = window.prompt('Why dismiss this response? (e.g. test submission, not ours)');
    if (reason) a.run(async () => (await api.post(`/api/door-hangers/${id}/dismiss`, { reason }), r.reload()));
  };
  const seen = (id: string) => a.run(async () => (await api.post(`/api/door-hangers/${id}/seen`), r.reload()));

  const answer = (d: any) => (
    <div className="dh-answer">
      <div>
        <strong>{d.name || 'No name given'}</strong>
        {d.phone && <> · {d.phone}</>}
        {d.email && <> · {d.email}</>}
      </div>
      <div className="small">{[d.address_line1, d.address_line2, d.city, d.zip].filter(Boolean).join(', ') || <span className="muted">No address given</span>}</div>
      {d.best_time && (
        <div>
          <span className="dh-k">Best time:</span> {d.best_time}
        </div>
      )}
      {d.message && (
        <div>
          <span className="dh-k">Message:</span> {d.message}
        </div>
      )}
      <div className="small muted">Received {when(d.received_at)}</div>
    </div>
  );

  return (
    <div className="stack">
      <div>
        <h1>Door hanger responses</h1>
        <p className="muted">
          When someone scans the QR code on a door hanger and fills in the form, it comes here. Responses for an address where a deputy left a hanger attach to that paper automatically.
        </p>
      </div>
      <div className="tabs">
        {(
          [
            ['unmatched', 'Needs matching'],
            ['matched', 'Matched'],
            ['dismissed', 'Dismissed'],
          ] as [Tab, string][]
        ).map(([k, l]) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => navigate(`/door-hangers?tab=${k}`)}>
            {l}
            {r.data && k === 'unmatched' && r.data.counts.unmatched > 0 && <span className="tab-count">{r.data.counts.unmatched}</span>}
            {r.data && k === 'matched' && r.data.counts.unseen > 0 && <span className="tab-count">{r.data.counts.unseen} new</span>}
          </button>
        ))}
      </div>
      <ErrorBox error={r.error ?? a.error} />
      {!r.data ? (
        <Spinner />
      ) : r.data.responses.length === 0 ? (
        <Empty>{tab === 'unmatched' ? 'Nothing waiting. Every response has found its paper.' : 'None yet.'}</Empty>
      ) : (
        r.data.responses.map((d: any) => (
          <Card key={d.id} className="dh-card">
            <div className="dh-row">
              {answer(d)}
              <div className="dh-side">
                {tab === 'unmatched' && (
                  <>
                    {d.suggestions.length > 0 ? (
                      <>
                        <div className="dh-k">Could be:</div>
                        {d.suggestions.map((s: any) => (
                          <div key={s.paper_id} className="dh-sugg">
                            <span>
                              <Link to={`/papers/${s.paper_id}`} className="mono">
                                {s.civilos_number}
                              </Link>{' '}
                              {s.defendant} <span className="small muted">· {s.line1}</span>
                              {s.hanger_left && (
                                <>
                                  {' '}
                                  <Badge tone="info">hanger left {new Date(s.last_hanger_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</Badge>
                                </>
                              )}
                            </span>
                            {manage && (
                              <button className="btn sm primary" onClick={() => match(d.id, s.paper_id)} disabled={a.busy}>
                                This one
                              </button>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="small muted">No open paper found at that address or under that name.</div>
                    )}
                    {manage && (
                      <form
                        className="row gap dh-manual"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (paperFor[d.id]?.trim()) match(d.id, paperFor[d.id]!.trim());
                        }}
                      >
                        <input aria-label="Paper number" placeholder="Paper number" value={paperFor[d.id] ?? ''} onChange={(e) => setPaperFor({ ...paperFor, [d.id]: e.target.value })} />
                        <button className="btn sm" disabled={a.busy || !paperFor[d.id]?.trim()}>
                          Attach
                        </button>
                        <button type="button" className="btn sm ghost" onClick={() => dismiss(d.id)} disabled={a.busy}>
                          Dismiss
                        </button>
                      </form>
                    )}
                  </>
                )}
                {tab === 'matched' && (
                  <>
                    <div>
                      On <Link to={`/papers/${d.paper_id}`} className="mono">{d.civilos_number}</Link> · {d.defendant}
                    </div>
                    <div className="small muted">
                      {d.matched_by_name ? `Attached by ${d.matched_by_name}` : `Matched automatically (${(d.matched_on ?? []).join(', ')})`}
                    </div>
                    {!d.seen_at ? (
                      <button className="btn sm" onClick={() => seen(d.id)} disabled={a.busy}>
                        Mark seen
                      </button>
                    ) : (
                      <Badge tone="ok">seen</Badge>
                    )}
                  </>
                )}
                {tab === 'dismissed' && <div className="small muted">Dismissed: {d.dismiss_reason}</div>}
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

/** Shown on the paper screen. */
export function DoorHangerCard({ responses }: { responses: any[] }) {
  if (!responses?.length) return null;
  return (
    <Card title="Door hanger response" className="dh-paper">
      {responses.map((d) => (
        <div key={d.id} className="dh-answer">
          <div>
            <strong>{d.name || 'No name given'}</strong>
            {d.phone && <> · {d.phone}</>}
            {d.email && <> · {d.email}</>}
          </div>
          {d.best_time && (
            <div>
              <span className="dh-k">Best time:</span> {d.best_time}
            </div>
          )}
          {d.message && (
            <div>
              <span className="dh-k">Message:</span> {d.message}
            </div>
          )}
          <div className="small muted">Received {when(d.received_at)}</div>
        </div>
      ))}
    </Card>
  );
}

/** Administration → Agency settings: the secret link Jotform posts to. */
export function DoorHangerLinkSettings() {
  const st = useLoad(() => api.get<any>('/api/admin/door-hanger-link'), []);
  const a = useAction();
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const make = () => {
    if (st.data?.configured && !window.confirm('Make a new link? The old link stops working, so you must paste the new one into Jotform.')) return;
    a.run(async () => {
      const r = await api.post<{ path: string }>('/api/admin/door-hanger-link');
      setUrl(window.location.origin + r.path);
      setCopied(false);
      st.reload();
    });
  };
  return (
    <Card title="Door hanger QR responses (Jotform)">
      <p className="small muted">
        Put the Jotform’s QR code on your door hangers. When someone fills it in, Jotform sends the answers to CivilHound and they attach to the paper for that address.
      </p>
      <ol className="small dh-steps">
        <li>
          In Jotform, make a form with: <strong>Full Name</strong>, <strong>Address</strong>, <strong>Phone Number</strong>, <strong>Best days and times to reach you</strong>, and{' '}
          <strong>Anything else we should know</strong>. Use Jotform’s own Full Name, Address and Phone fields.
        </li>
        <li>Click the button below and copy the link.</li>
        <li>
          In Jotform: <em>Settings → Integrations → WebHooks</em>, paste the link and save.
        </li>
        <li>Print the form’s QR code (Jotform: Publish → QR Code) on the door hanger.</li>
      </ol>
      {st.data && (
        <p className="small">
          {st.data.configured ? (
            <>
              Link made {new Date(st.data.createdAt).toLocaleDateString()}.{' '}
              {st.data.lastReceivedAt ? `Last response ${new Date(st.data.lastReceivedAt).toLocaleString()}.` : 'No responses received yet.'}
            </>
          ) : (
            'No link yet.'
          )}
        </p>
      )}
      {url && (
        <div className="dh-link">
          <input readOnly value={url} aria-label="Webhook link" onFocus={(e) => e.target.select()} />
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              navigator.clipboard?.writeText(url).then(() => setCopied(true), () => undefined);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <div className="small warn-text">Copy it now. For security it won’t be shown again. If you lose it, make a new one.</div>
        </div>
      )}
      <button type="button" className="btn" onClick={make} disabled={a.busy}>
        {st.data?.configured ? 'Make a new link' : 'Make the link'}
      </button>
      <ErrorBox error={a.error ?? st.error} />
    </Card>
  );
}
